// src/features/auth/components/UserGrid.jsx
import React, { useState, useMemo } from 'react';
import { Typography, Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import UserCard from './UserCard';

const LETTER_RANGES = [
  { label: 'A–E', from: 'A', to: 'E' },
  { label: 'F–J', from: 'F', to: 'J' },
  { label: 'K–O', from: 'K', to: 'O' },
  { label: 'P–T', from: 'P', to: 'T' },
  { label: 'U–Z', from: 'U', to: 'Z' },
  { label: 'Æ–Å', from: 'Æ', to: 'Å' },
];

function inRange(name, from, to) {
  const letter = name?.charAt(0).toUpperCase() ?? '';
  return letter >= from && letter <= to;
}

export default function UserGrid({ users, onUserSelect, dark = false }) {
  const { t } = useTranslation(['auth']);
  const [activeRange, setActiveRange] = useState(null);

  const activeRanges = LETTER_RANGES;

  const filtered = useMemo(
    () => activeRange ? users.filter(u => inRange(u.name, activeRange.from, activeRange.to)) : users,
    [users, activeRange]
  );

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mt: 3, mb: 2 }}>
        {activeRanges.length > 1 ? (
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label={t('auth:change_user.filter_all')}
              color="info"
              variant={activeRange === null ? 'filled' : 'outlined'}
              onClick={() => setActiveRange(null)}
              sx={{ height: 40, fontSize: '0.9rem', px: 1.9 }}
            />
            {activeRanges.map(r => (
              <Chip
                key={r.label}
                label={r.label}
                color="info"
                variant={activeRange?.label === r.label ? 'filled' : 'outlined'}
                onClick={() => setActiveRange(activeRange?.label === r.label ? null : r)}
                sx={{ height: 40, fontSize: '0.9rem', px: 1.9 }}
              />
            ))}
          </Box>
        ) : <Box />}

        <Typography variant="h6" sx={{ display: { xs: 'none', sm: 'block' }, ...(dark ? { color: 'rgba(255,255,255,0.7)' } : {}) }}>
          {t('auth:change_user.available_staff')} ({filtered.length})
        </Typography>
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(250px, 320px))"
        gap={3}
      >
        {filtered.map((user) => (
          <UserCard key={user.id ?? user.name} user={user} onSelect={onUserSelect} />
        ))}
      </Box>
    </>
  );
}